import React from 'react';

import { Grid } from '@mui/material';
import { DateTime } from 'luxon';

import { Field, Form } from '@hrlogics/react-components/form';

import { DateFormatPatterns, STATUSES_OPTIONS } from '@clearverify/constants';
import { formatPhoneNumber } from '@clearverify/utils';

import { PeoPayrollProviderStaffDocument, UserStatus, useChangeUserStatusMutation } from 'src/api';
import { Button, DetailGridItem, FullscreenLoader } from 'src/components/common';
import { SelectField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { usePeoPayrollProviderStaff } from 'src/hooks/peoPayrollProviderStaff';
import { useCurrentUser } from 'src/hooks/user';
import { isEnableStatus } from 'src/utils/user';

import { FormFields, FormValues } from './types';

export interface PeoPayrollProviderStaffDetailsModalFormProps {
  onCloseModal: () => void;
  peoPayrollProviderStaffId: string | undefined;
  switchClosability: (closability: boolean) => void;
}

export const PeoPayrollProviderStaffDetailsModalForm: React.FC<
  PeoPayrollProviderStaffDetailsModalFormProps
> = ({ switchClosability, onCloseModal, peoPayrollProviderStaffId }) => {
  const { createApolloContext } = useNotification();

  const {
    peoPayrollProviderStaff,
    isPeoPayrollProviderUser: isPEOPayrollProviderStaffUser,
    loading,
  } = usePeoPayrollProviderStaff(peoPayrollProviderStaffId);

  const { user: currentUser, isUserLoading } = useCurrentUser();

  const [changeUserStatus] = useChangeUserStatusMutation({
    refetchQueries: [
      { query: PeoPayrollProviderStaffDocument, variables: { id: peoPayrollProviderStaffId } },
    ],
    context: createApolloContext({
      errorMessage: 'Failed to update Peo Payroll Provider Staff',
      successMessage: 'Peo Payroll Provider Staff has been successfully updated',
    }),
  });

  const handleSubmit = React.useCallback(
    async (formValues: FormValues) => {
      try {
        await changeUserStatus({
          variables: {
            userId: peoPayrollProviderStaff?.user.id || '',
            newStatus: formValues.status,
          },
        });

        switchClosability(true);
        onCloseModal();
      } catch (error) {
        console.error(error);
        switchClosability(true);
      }
    },
    [changeUserStatus, peoPayrollProviderStaff?.user.id, onCloseModal, switchClosability],
  );

  const { firstName, lastName, birthDate, phoneNumber, email, status } =
    peoPayrollProviderStaff?.user ?? {};

  const initialValues: FormValues = React.useMemo(
    () => ({
      [FormFields.userStatus]: peoPayrollProviderStaff?.user.status || UserStatus.Inactive,
    }),
    [peoPayrollProviderStaff?.user.status],
  );

  const shouldRenderStatusField =
    isPEOPayrollProviderStaffUser &&
    peoPayrollProviderStaff?.user.id !== currentUser?.id &&
    isEnableStatus(status);

  if (loading || isUserLoading) {
    return <FullscreenLoader />;
  }

  return (
    <Form initialValues={initialValues} onSubmit={handleSubmit}>
      {({ dirty, submitting }) => (
        <Grid container spacing={1.5}>
          <Grid size={12}>
            <DetailGridItem topText="First Name" bottomText={firstName} />
          </Grid>
          <Grid size={12}>
            <DetailGridItem topText="Last Name" bottomText={lastName} />
          </Grid>
          <Grid size={12}>
            <DetailGridItem topText="Email Address" bottomText={email} />
          </Grid>
          <Grid size={12}>
            <DetailGridItem
              topText="Date of birth"
              bottomText={
                birthDate &&
                DateTime.fromISO(birthDate).toFormat(DateFormatPatterns.monthDayYearDots)
              }
            />
          </Grid>
          <Grid size={12}>
            <DetailGridItem topText="Phone Number" bottomText={formatPhoneNumber(phoneNumber)} />
          </Grid>

          {shouldRenderStatusField && (
            <Grid my={2} spacing={2} size={12}>
              <Field
                name={FormFields.userStatus}
                component={SelectField}
                label="Status"
                options={STATUSES_OPTIONS}
                fullWidth
              />
            </Grid>
          )}

          <Grid container spacing={3} justifyContent="center">
            <Grid>
              <Button onClick={onCloseModal} variant="outlined" color="error">
                Close
              </Button>
            </Grid>
            {shouldRenderStatusField && (
              <Grid>
                <Button
                  variant="contained"
                  type="submit"
                  loading={submitting}
                  disabled={!dirty || submitting}
                >
                  Save Changes
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
    </Form>
  );
};
